import type { ChildMeta, EntityMeta, Workflow, WorkflowStage } from "@wms/domain";

/**
 * ТЗ «Понятные шаги: UI/UX без потери функций» (30.10.2026), section 20: the pure part of the stepwise card.
 * Steps are a projection of what already exists — the parts of the input (the groups of the guided layout) and the
 * stages the server returns in GET /{entity}/{id}/workflow with their tab, form and action codes. Nothing here is a
 * business state: a step number is not a status (UX1-007), the truth is the server's workflow and the record.
 */

export type CardView = "steps" | "full";

/** A part of the input: «Основное» or a titled group of the guided layout (UX1-021). */
export interface FormPart {
  key: string;
  label: string;
  help?: string | null;
}

export interface CardStep {
  key: string;
  label: string;
  /** The state of the stage from the server; «input» for a part of the form of a record without a workflow. */
  state: WorkflowStage["state"] | "input";
  hint?: string | null;
  /** form — the whole header form; part — one part of the input; tab — a child list; actions — only actions. */
  kind: "form" | "part" | "tab" | "actions";
  part: string | null;
  tab: string | null;
  actions: string[];
}

export const MAIN_PART = "main";

/** The parts of the input exactly as EntityForm lays them out: «Основное» plus every open, non-source group. */
export function formParts(meta: EntityMeta | undefined, guided: boolean): FormPart[] {
  const parts: FormPart[] = [{ key: MAIN_PART, label: "Основное", help: null }];
  if (!meta || !guided) return parts;
  for (const group of meta.groups ?? []) {
    if (group.source || group.collapsed) continue;
    if (!meta.fields.some((f) => f.group === group.key && !f.hidden && !f.static)) continue;
    parts.push({ key: group.key, label: group.label, help: group.help ?? null });
  }
  return parts;
}

/**
 * The steps of a card. With a workflow every stage is a step and names what it is made of; without one (a new
 * record, a reference item) the parts of the input are the steps — no server state is invented (section 6, last row).
 */
export function buildSteps(args: { workflow?: Workflow | null; parts: FormPart[]; children: ChildMeta[] }): CardStep[] {
  const { workflow, parts, children } = args;
  const tabs = new Set(children.map((c) => c.entity));
  if (workflow && workflow.stages.length > 0) {
    return workflow.stages.map((stage) => {
      const tab = stage.tab && tabs.has(stage.tab) ? stage.tab : null;
      const actions = stage.actions ?? [];
      const kind: CardStep["kind"] = stage.form ? "form" : tab ? "tab" : "actions";
      return { key: stage.key, label: stage.label, state: stage.state, hint: stage.hint ?? null, kind, part: null, tab, actions };
    });
  }
  return parts.map((part) => ({
    key: `part:${part.key}`,
    label: part.label,
    state: "input",
    hint: part.help ?? null,
    kind: "part",
    part: part.key,
    tab: null,
    actions: [],
  }));
}

/** The step a field of the form belongs to: the step of its part, or the form step of a saved document. */
export function stepOfPart(steps: CardStep[], part: string): CardStep | undefined {
  return steps.find((s) => s.kind === "part" && s.part === part) ?? steps.find((s) => s.kind === "form");
}

export function stepOfTab(steps: CardStep[], tab: string): CardStep | undefined {
  return steps.find((s) => s.tab === tab);
}

export function stepOfAction(steps: CardStep[], code: string): CardStep | undefined {
  return steps.find((s) => s.actions.includes(code));
}

/**
 * Which step opens: the one asked for by the deep link when it exists (UX1-009 — validated, never trusted), else the
 * current or waiting stage, else where the recommendation points, else the first step that is not done.
 */
export function defaultStep(steps: CardStep[], requested: string | null | undefined, workflow?: Workflow | null): string {
  if (!steps.length) return "";
  if (requested && steps.some((s) => s.key === requested)) return requested;
  const live = steps.find((s) => s.state === "current" || s.state === "waiting");
  if (live) return live.key;
  const next = workflow?.recommended;
  if (next?.kind === "tab" && next.key) {
    const step = stepOfTab(steps, next.key);
    if (step) return step.key;
  }
  if (next?.kind === "action" && next.code) {
    const step = stepOfAction(steps, next.code);
    if (step) return step.key;
  }
  const open = steps.find((s) => s.state !== "done" && s.state !== "skipped");
  return (open ?? steps[steps.length - 1]).key;
}

/** Neighbours for «Назад» / «Далее»: plain moves along the strip, no business meaning (UX1-007). */
export function neighbours(steps: CardStep[], key: string): { prev: CardStep | null; next: CardStep | null } {
  const index = steps.findIndex((s) => s.key === key);
  if (index < 0) return { prev: null, next: null };
  return { prev: index > 0 ? steps[index - 1] : null, next: index < steps.length - 1 ? steps[index + 1] : null };
}

/** What the strip says about a stage that is not the current one (section 6). */
export function stateWords(state: CardStep["state"]): string {
  switch (state) {
    case "done":
      return "Выполнено";
    case "current":
      return "Текущий шаг";
    case "waiting":
      return "Ожидает другого сотрудника";
    case "skipped":
      return "Не требуется";
    case "future":
      return "Будет доступен позже";
    default:
      return "";
  }
}

/** The stored choice of the card view for this browser; the URL wins over it, the option wins over both. */
export function readStoredView(): CardView | null {
  try {
    const stored = localStorage.getItem("wms-card-view");
    return stored === "full" || stored === "steps" ? stored : null;
  } catch {
    return null;
  }
}

export function storeView(view: CardView): void {
  try {
    localStorage.setItem("wms-card-view", view);
  } catch {
    /* private mode */
  }
}

export function parseView(raw: string | null | undefined): CardView | null {
  return raw === "full" || raw === "steps" ? raw : null;
}
